import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { mergeMap, Observable, of, map, catchError, tap, EMPTY } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import {
  DigitalSignatureResult,
  DprReportSignRequestStatus,
  SendSignRequest,
  ServiceResult,
  Signer,
} from '@dpr/models/digital-signature';
import {
  HttpDprContext,
  StaticMessage,
  UtcDateTimeOptions,
} from '@shared/enums';
import {
  Constants,
  convertDateToString,
  getStaticMessage,
  toUTCDate,
} from '@shared/utils';
import { ConfirmDialogComponent } from '@shared/components';
import { DataRefreshService, GenericHttpClientService } from '@shared/services';

@UntilDestroy()
@Injectable()
export class DigitalSignatureService {
  constructor(
    private genericHttpClientService: GenericHttpClientService,
    private dialog: MatDialog,
    private dataRefreshService: DataRefreshService,
    private matDialog: MatDialog
  ) {}

  copySigners(date: Date): Observable<Signer[]> {
    return this.getPreviousDprSigners(date).pipe(
      map((result) =>
        result.map((signer) => {
          return {
            email: signer,
            canBeDeleted: true,
          };
        })
      )
    );
  }

  getDprSignRequestStatus(
    dprReportSignRequestId: number
  ): Observable<DprReportSignRequestStatus> {
    return this.genericHttpClientService
      .get<ServiceResult<DprReportSignRequestStatus>>({
        context: HttpDprContext.GetDprSignRequestStatus,
        args: {
          dprReportSignRequestId,
        },
      })
      .pipe(
        mergeMap((result) => {
          return of(result.value);
        }),
        catchError((error: HttpErrorResponse) => {
          this.onError(error);
          return EMPTY;
        })
      );
  }

  sendDprSignRequest(
    model: SendSignRequest
  ): Observable<DigitalSignatureResult> {
    return this.genericHttpClientService
      .post<ServiceResult<number>, SendSignRequest>({
        context: HttpDprContext.SendDprSignRequest,
        body: model,
      })
      .pipe(
        mergeMap((result) =>
          of({
            hasActiveSignRequest: true,
            dprReportSignRequestId: result.value,
          })
        ),
        catchError((error: HttpErrorResponse) => {
          this.onError(error);
          return EMPTY;
        })
      );
  }

  cancelDprSignRequest(
    dprReportSignRequestId: number
  ): Observable<DigitalSignatureResult> {
    return this.genericHttpClientService
      .post<void, number>({
        context: HttpDprContext.CancelDprSignRequest,
        args: {
          dprReportSignRequestId,
        },
      })
      .pipe(
        mergeMap(() =>
          of({
            hasActiveSignRequest: false,
            dprReportSignRequestId: null,
          })
        ),
        catchError((error: HttpErrorResponse) => {
          this.onError(error);
          return EMPTY;
        })
      );
  }

  private getPreviousDprSigners(date: Date): Observable<string[]> {
    const utcDate = convertDateToString(
      toUTCDate(new Date(date), UtcDateTimeOptions.UtcDateWithoutTime)
    );
    return this.genericHttpClientService.get<string[]>({
      context: HttpDprContext.GetPreviousDprSigners,
      args: {
        utcDate,
      },
    });
  }

  private onError(error: HttpErrorResponse): void {
    if (error.status === 500) {
      // 500 is handled by http interceptor
      return;
    }
    this.showErrorModal(error.error);
  }

  private showErrorModal(message: string): void {
    const msg =
      `${message}</br></br> ` +
      $localize`:@@dpr.digitalSignatureModal.txtClickOkToRefresh:Click <b>OK</b> to refresh.`;

    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      ...Constants.DialogOptions,
      data: {
        title: getStaticMessage(StaticMessage.ErrorTitle),
        message: msg,
        buttonLabel: getStaticMessage(StaticMessage.OkButton),
        hideCancelButton: true,
      },
    });

    confirmDialog
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        tap(() => {
          this.matDialog.closeAll();
          this.dataRefreshService.setRefreshNeeded();
        })
      )
      .subscribe();
  }
}
